import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';
import { clearCustomerPortalSession } from '../utils/customerPortalStorage';

const AuthContext = createContext(null);

/**
 * Laravel JsonResponseHelper envelope: { success, data: T, message }
 */
function unwrapApiBody(body) {
  if (body && typeof body === 'object' && 'data' in body && body.data !== undefined) {
    return body.data;
  }
  return body;
}

/**
 * GET /customer/me and POST /customer/login return { customer, organization }.
 */
function parseCustomerSessionPayload(inner) {
  if (inner && typeof inner === 'object' && 'customer' in inner) {
    return {
      customer: inner.customer,
      organization: inner.organization ?? null,
    };
  }
  return { customer: inner, organization: null };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const restoreSession = useCallback(async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const body = await authService.verifyToken();
      const inner = unwrapApiBody(body);
      const { customer, organization: org } = parseCustomerSessionPayload(inner);
      setUser(customer);
      setOrganization(org);
      localStorage.setItem('customer_user', JSON.stringify(customer));
    } catch {
      clearCustomerPortalSession({ preserveOrgPublicId: true });
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  const login = useCallback(async (email, password, organizationPublicId) => {
    const body = await authService.login(email, password, organizationPublicId);
    const inner = unwrapApiBody(body);
    const { token, customer, organization: org } = inner;

    localStorage.setItem('customer_token', token);
    localStorage.setItem('organization_public_id', organizationPublicId);
    localStorage.setItem('customer_user', JSON.stringify(customer));

    setUser(customer);
    setOrganization(org ?? null);
    return { customer, organization: org };
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    clearCustomerPortalSession();
    setUser(null);
    setOrganization(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('customer_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const refreshOrganization = useCallback(async () => {
    const body = await authService.verifyToken();
    const inner = unwrapApiBody(body);
    const { organization: org } = parseCustomerSessionPayload(inner);
    setOrganization(org);
  }, []);

  /**
   * Memoize the context value so that re-renders of AuthProvider (e.g. during
   * session restore) do NOT propagate to every useAuth() consumer unless the
   * underlying state actually changed. Without this, a new object is created on
   * every render, causing ProtectedRoute, AuthenticatedLayout, and every other
   * consumer to re-render unnecessarily.
   */
  const contextValue = useMemo(() => {
    const organizationName        = organization?.name          ?? '';
    const organizationLogo        = organization?.logo_url      ?? '';
    const organizationCover       = organization?.cover_url     ?? '';
    const organizationShowLogo    = Boolean(organization?.show_logo);
    const organizationShowCover   = Boolean(organization?.show_cover);
    const organizationTagline     = organization?.tagline       ?? '';
    const organizationDescription = organization?.description   ?? '';
    const organizationPhone       = organization?.phone         ?? '';
    const organizationEmail       = organization?.email         ?? '';
    const organizationWebsiteUrl  = organization?.website_url   ?? '';
    const organizationWebsiteName = organization?.website_name  ?? '';

    const organizationPublicIdentifiers = Array.isArray(organization?.public_identifiers)
      ? organization.public_identifiers.filter(
          (row) =>
            row &&
            String(row.label ?? '').trim() !== '' &&
            String(row.value ?? '').trim() !== '',
        )
      : [];

    /** All business web pages for portal UI (falls back to single website_* when array absent). */
    const organizationWebsites =
      Array.isArray(organization?.websites) && organization.websites.length > 0
        ? organization.websites
            .filter((w) => w && String(w.url ?? '').trim())
            .map((w) => {
              const url    = String(w.url).trim();
              const rawName = w.name != null && String(w.name).trim() ? String(w.name).trim() : '';
              const rawTag  = w.tag  != null && String(w.tag).trim()  ? String(w.tag).trim()  : '';
              return { url, name: rawName || url, ...(rawTag ? { tag: rawTag } : {}) };
            })
        : organizationWebsiteUrl
          ? [{ url: organizationWebsiteUrl, name: organizationWebsiteName || organizationWebsiteUrl }]
          : [];

    return {
      user,
      organization,
      loading,
      isAuthenticated: !!user,
      organizationName,
      organizationLogo,
      organizationCover,
      organizationShowLogo,
      organizationShowCover,
      organizationTagline,
      organizationDescription,
      organizationPublicIdentifiers,
      organizationPhone,
      organizationEmail,
      organizationWebsiteUrl,
      organizationWebsiteName,
      organizationWebsites,
      login,
      logout,
      updateUser,
      refreshOrganization,
    };
  }, [user, organization, loading, login, logout, updateUser, refreshOrganization]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
